import { Injectable, computed, inject, signal } from '@angular/core';
import { finalize, Observable, tap } from 'rxjs';
import { LandingFieldKey, LandingFormFieldDto } from '@campaigns/data-access';
import {
  LandingPublicApiService,
  LoyaltySessionDto,
  PromoCustomerDto,
  PromoCustomerProfileDto,
} from '@campaign-landing/data-access';
import { LandingConfigService } from './landing-config.service';

const BIRTH_DATE_FIELD: LandingFormFieldDto = {
  key: LandingFieldKey.DateOfBirth,
  label: 'Date of Birth',
  visible: true,
  required: true,
  custom: false,
};

@Injectable()
export class LandingCustomerService {
  private readonly landingPublicApiService = inject(LandingPublicApiService);
  private readonly landingConfigService = inject(LandingConfigService);

  readonly customer = signal<PromoCustomerProfileDto | null>(null);
  readonly isRedirecting = signal(false);
  readonly isProfileComplete = signal(false);

  readonly missingFields = computed(() => {
    const customer = this.customer();
    const config = this.landingConfigService.config();

    if (!customer || !config) {
      return [];
    }

    const values: Partial<Record<string, string | null>> = {
      ...customer.customFields,
      [LandingFieldKey.FirstName]: customer.firstName,
      [LandingFieldKey.LastName]: customer.lastName,
      [LandingFieldKey.Email]: customer.email,
      [LandingFieldKey.DateOfBirth]: customer.birthDate,
    };

    return this.profileFields(config.fields).filter(
      (field) => field.visible && !values[field.key],
    );
  });

  readonly needsProfileCompletion = computed(
    () =>
      !this.isProfileComplete() ||
      this.missingFields().some((field) => field.required),
  );

  setCustomer(customer: PromoCustomerDto): void {
    this.customer.set({
      email: customer.email,
      firstName: customer.firstName,
      lastName: customer.lastName,
      birthDate: customer.birthDate,
      customFields: customer.customFields
        ? (JSON.parse(customer.customFields) as Record<string, string>)
        : {},
    });
  }

  redirectToLoyalty(): Observable<LoyaltySessionDto> {
    this.isRedirecting.set(true);

    return this.landingPublicApiService.createLoyaltySession().pipe(
      tap((session) => window.location.assign(session.redirectUrl)),
      finalize(() => this.isRedirecting.set(false)),
    );
  }

  private profileFields(fields: LandingFormFieldDto[]): LandingFormFieldDto[] {
    const withBirthDate = fields.some(
      (field) => field.key === LandingFieldKey.DateOfBirth,
    )
      ? fields
      : [BIRTH_DATE_FIELD, ...fields];

    return withBirthDate.map((field) =>
      field.key === LandingFieldKey.DateOfBirth
        ? { ...field, visible: true, required: true }
        : field,
    );
  }
}
